import { useMemo, useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiLoading, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { $api } from '../api/queryClient'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { Alert, AlertDescription } from './ui/alert'
import { DerivationInputsField } from './derivations/DerivationInputsField'
import { useAssetTypes } from '../api/queries'

function invalidateDerivations(queryClient: ReturnType<typeof useQueryClient>, _org: string) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1]
        return path === '/api/v1/organizations/{orgCode}/derivations'
      }
      return false
    }
  })
}

export function DerivationsPage() {
  const { org } = useParams({ strict: false })
  const queryClient = useQueryClient()
  const { t } = useTranslation('derivations')
  const [searchQuery, setSearchQuery] = useState('')
  const [enabledFilter, setEnabledFilter] = useState<string>('all')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const emptyDerivation = {
    code: '',
    name: { en: '', fr: '' },
    description: '',
    target_property_code: '',
    enabled: true,
    // A derivation is written as a decision table or as a script (spec 2026-10-06-13).
    kind: 'decision_table' as 'decision_table' | 'script',
    decision_table: '[\n  {\n    "priority": 10,\n    "comment": "",\n    "conditions": {},\n    "output": ""\n  }\n]',
    script: 'return null;',
    inputs: [] as string[],
  }
  const [newDerivation, setNewDerivation] = useState(emptyDerivation)
  const [createError, setCreateError] = useState('')
  const { data: assetTypes = [] } = useAssetTypes(org!)

  useBreadcrumbs(useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb') }
  ], [org, t]))

  const queryParams: Record<string, any> = {}
  if (searchQuery) queryParams.q = searchQuery
  if (enabledFilter !== 'all') queryParams.enabled = enabledFilter === 'true'

  const { data, isLoading, error } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/derivations', {
    params: {
      path: { orgCode: org! },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    }
  })

  const createMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/derivations', {
    onSuccess: () => {
      invalidateDerivations(queryClient, org!)
      setIsCreateDialogOpen(false)
      setNewDerivation(emptyDerivation)
      setCreateError('')
    },
    onError: (err: any) => {
      const messages = [err?.title, ...((err?.fields || []) as any[]).map((f: any) => f.message)].filter(Boolean)
      setCreateError(Array.from(new Set(messages)).join(' — '))
    }
  })

  const derivations = (data as any)?.data || []
  const total = (data as any)?.total || 0

  const handleCreate = () => {
    let definition: Record<string, unknown>
    if (newDerivation.kind === 'decision_table') {
      try {
        definition = { decision_table: JSON.parse(newDerivation.decision_table) }
      } catch {
        setCreateError(t('create_dialog.invalid_json'))
        return
      }
    } else {
      definition = { script: newDerivation.script, inputs: newDerivation.inputs }
    }
    setCreateError('')

    createMutation.mutate({
      params: { path: { orgCode: org! } },
      body: {
        code: newDerivation.code,
        name: newDerivation.name,
        description: newDerivation.description || undefined,
        target_property_code: newDerivation.target_property_code,
        enabled: newDerivation.enabled,
        ...definition,
      } as any
    })
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.derivations}
            title={t('page_title')}
            description={t('page_description', { count: total })}
            actions={
              <>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <Can perm="configuration.write">
              <DialogTrigger asChild>
                <Button data-testid="create-derivation-btn">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {t('create_button')}
                </Button>
              </DialogTrigger>
            </Can>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('create_dialog.title')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 p-4">
                <div>
                  <Label htmlFor="code">{t('create_dialog.code_label')}</Label>
                  <Input
                    id="code"
                    placeholder="derive_fire_rating"
                    value={newDerivation.code}
                    onChange={(e) => setNewDerivation(prev => ({ ...prev, code: e.target.value }))}
                    data-testid="create-derivation-code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name-en">{t('create_dialog.name_en_label')}</Label>
                    <Input
                      id="name-en"
                      value={newDerivation.name.en}
                      onChange={(e) => setNewDerivation(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                      data-testid="create-derivation-name-en"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name-fr">{t('create_dialog.name_fr_label')}</Label>
                    <Input
                      id="name-fr"
                      value={newDerivation.name.fr}
                      onChange={(e) => setNewDerivation(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))}
                      data-testid="create-derivation-name-fr"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">{t('create_dialog.description_label')}</Label>
                  <Textarea
                    id="description"
                    value={newDerivation.description}
                    onChange={(e) => setNewDerivation(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="create-derivation-description"
                  />
                </div>
                <div>
                  <Label htmlFor="target-property">{t('create_dialog.target_property_label')}</Label>
                  <Input
                    id="target-property"
                    placeholder="ck_fire_rating"
                    value={newDerivation.target_property_code}
                    onChange={(e) => setNewDerivation(prev => ({ ...prev, target_property_code: e.target.value }))}
                    data-testid="create-derivation-target"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={newDerivation.enabled}
                    onCheckedChange={(checked) => setNewDerivation(prev => ({ ...prev, enabled: checked === true }))}
                    data-testid="create-derivation-enabled"
                  />
                  <Label>{t('create_dialog.enabled_label')}</Label>
                </div>
                <div>
                  <Label>{t('create_dialog.kind_label')}</Label>
                  <Select
                    value={newDerivation.kind}
                    onValueChange={(v) => setNewDerivation(prev => ({ ...prev, kind: v as 'decision_table' | 'script' }))}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="create-derivation-kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="decision_table" data-testid="create-derivation-kind-table">{t('create_dialog.kind_table')}</SelectItem>
                      <SelectItem value="script" data-testid="create-derivation-kind-script">{t('create_dialog.kind_script')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newDerivation.kind === 'decision_table' ? (
                  <div>
                    <Label htmlFor="decision-table">{t('create_dialog.rules_label')}</Label>
                    <Textarea
                      id="decision-table"
                      className="font-mono text-sm min-h-[200px]"
                      value={newDerivation.decision_table}
                      onChange={(e) => setNewDerivation(prev => ({ ...prev, decision_table: e.target.value }))}
                      data-testid="create-derivation-rules"
                    />
                  </div>
                ) : (
                  <>
                    <DerivationInputsField
                      org={org!}
                      value={newDerivation.inputs}
                      onChange={(inputs) => setNewDerivation(prev => ({ ...prev, inputs }))}
                      assetTypes={assetTypes as any[]}
                    />
                    <div>
                      <Label htmlFor="script">{t('create_dialog.script_label')}</Label>
                      <Textarea
                        id="script"
                        className="font-mono text-sm min-h-[160px]"
                        value={newDerivation.script}
                        onChange={(e) => setNewDerivation(prev => ({ ...prev, script: e.target.value }))}
                        data-testid="create-derivation-script"
                      />
                    </div>
                  </>
                )}
                {createError && (
                  <Alert variant="destructive" data-testid="create-derivation-error">
                    <AlertDescription>{createError}</AlertDescription>
                  </Alert>
                )}
                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  data-testid="create-derivation-submit"
                  className="w-full"
                >
                  {createMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : null}
                  {t('create_dialog.submit')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
              </>
            }
          />
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-4">
            <Input
              placeholder={t('search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="derivations-search"
            />
            <Select value={enabledFilter} onValueChange={setEnabledFilter}>
              <SelectTrigger className="w-[180px]" data-testid="derivations-enabled-filter">
                <SelectValue placeholder={t('filter_status_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:status.all')}</SelectItem>
                <SelectItem value="true">{t('common:status.enabled')}</SelectItem>
                <SelectItem value="false">{t('common:status.disabled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">{t('failed_to_load')}</div>
          ) : derivations.length === 0 ? (
            <EmptyState variant="inline" live title={t('no_derivations')} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.name')}</TableHead>
                  <TableHead>{t('table.code')}</TableHead>
                  <TableHead>{t('table.target_property')}</TableHead>
                  <TableHead>{t('table.inputs')}</TableHead>
                  <TableHead>{t('table.kind')}</TableHead>
                  <TableHead>{t('table.enabled')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {derivations.map((d: any) => (
                  <TableRow key={d.uid}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link
                          to="/$org/derivations/$uid"
                          params={{ org: org!, uid: d.uid }}
                          className="text-primary hover:underline font-medium"
                          data-testid={`derivation-link-${d.code}`}
                        >
                          {d.name?.en || d.name?.fr || d.code}
                        </Link>
                        {d.last_error && (
                          <Badge
                            variant="destructive"
                            title={d.last_error}
                            data-testid={`derivation-error-badge-${d.code}`}
                          >
                            {t('last_error.badge')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-1 py-0.5 rounded">{d.code}</code>
                    </TableCell>
                    <TableCell>
                      {d.target_property ? (
                        <span title={d.target_property.code}>
                          {d.target_property.name?.en || d.target_property.code}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="whitespace-nowrap">{t('badges.inputs_count', { count: d.inputs?.length || 0 })}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="whitespace-nowrap" data-testid={`derivation-kind-${d.code}`}>
                        {d.kind === 'script'
                          ? t('badges.script')
                          : `${t('badges.decision_table')} · ${t('badges.rules_count', { count: d.rules_count || 0 })}`}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={d.enabled ? 'default' : 'secondary'}>
                        {d.enabled ? t('common:status.enabled') : t('common:status.disabled')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}
